import React from "react";
import { Container, Row, Col, Card } from "react-bootstrap";
import { motion } from "framer-motion";
import bgImage from "../assets/optBack2.png";
import InvestmentImage from "../assets/opt1.jpeg";
import CommercialImage from "../assets/opt2.jpg";
import WhyUs1 from "../assets/optSer1.jpeg";
import WhyUs2 from "../assets/optSer2.jpeg";
import WhyUs3 from "../assets/optSer3.jpg";
import WhyUs4 from "../assets/optSer4.jpg";
import "bootstrap/dist/css/bootstrap.min.css";
import { useNavigate } from "react-router-dom";

const fadeIn = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
};

const slideInLeft = {
  hidden: { opacity: 0, x: -50 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.7 } },
};

const slideInRight = {
  hidden: { opacity: 0, x: 50 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.7 } },
};

const HomePage = () => {
  const navigate = useNavigate();

  const professionalStyles = {
    heroSection: {
      background: `linear-gradient(rgba(0, 31, 63, 0.85), rgba(0, 51, 102, 0.85)), url(${bgImage})`,
      backgroundSize: "cover",
      backgroundPosition: "center",
      padding: "100px 50px",
      position: "relative",
      overflow: "hidden",
    },
    heroTitle: {
      fontSize: "3.5rem",
      fontWeight: "700",
      letterSpacing: "-0.5px",
      marginBottom: "25px",
      lineHeight: "1.2",
    },
    heroText: {
      fontSize: "1.2rem",
      maxWidth: "900px",
      margin: "0 auto 40px",
      lineHeight: "1.8",
      fontWeight: "300",
    },
    ctaButton: {
      padding: "15px 45px",
      fontSize: "1.1rem",
      fontWeight: "600",
      letterSpacing: "0.5px",
      border: "2px solid #fff",
      background: "transparent",
      color: "#fff",
      transition: "all 0.3s ease",
      textTransform: "uppercase",
    },
    sectionTitle: {
      fontSize: "2.5rem",
      fontWeight: "700",
      marginBottom: "60px",
      position: "relative",
      paddingBottom: "15px",
      borderBottom: "4px solid #003366",
      letterSpacing: "-0.5px",
      textAlign: "center",
      margin: "0 auto 60px",
      display: "table",
    },
    card: {
      border: "none",
      borderLeft: "4px solid #003366",
      background: "#fff",
      transition: "all 0.3s ease",
      height: "100%",
      boxShadow: "0 2px 10px rgba(0,0,0,0.08)",
      borderRadius: "0",
      overflow: "hidden",
    },
    cardImage: {
      width: "100%",
      height: "240px",
      objectFit: "cover",
      borderRadius: "0",
    },
    cardBody: {
      padding: "30px 25px",
      display: "flex",
      flexDirection: "column",
      minHeight: "220px",
    },
    cardTitle: {
      fontSize: "1.3rem",
      fontWeight: "700",
      marginBottom: "20px",
      color: "#003366",
      textTransform: "uppercase",
      letterSpacing: "0.5px",
    },
    cardText: {
      fontSize: "0.95rem",
      lineHeight: "1.7",
      color: "#555",
    },
    serviceImage: {
      width: "100%",
      height: "400px",
      objectFit: "cover",
      boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
      transition: "all 0.4s ease",
      borderRadius: "0",
    },
    serviceTitle: {
      fontSize: "2.3rem",
      fontWeight: "700",
      marginBottom: "25px",
      color: "#003366",
      letterSpacing: "-0.5px",
      borderLeft: "6px solid #003366",
      paddingLeft: "20px",
    },
    serviceText: {
      fontSize: "1.15rem",
      lineHeight: "1.9",
      color: "#444",
      fontWeight: "400",
    },
    whyUsSection: {
      background: "#f8f9fa",
      padding: "80px 50px",
      marginTop: "60px",
      marginBottom: "60px",
    },
  };

  return (
    <Container fluid style={{ padding: 0 }}>
      {/* Hero Section */}
      <motion.div
        initial="hidden"
        animate="visible"
        variants={fadeIn}
        className="text-center text-white"
        style={professionalStyles.heroSection}
      >
        <motion.h1
          style={professionalStyles.heroTitle}
          initial={{ opacity: 0, y: -30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.8 }}
        >
          OPTWISE GROUP
        </motion.h1>
        <motion.p
          style={professionalStyles.heroText}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4, duration: 0.8 }}
        >
          Australia's premier investment firm specializing in fiduciary and
          administration services. We deliver exceptional returns through
          strategic asset management and comprehensive investment solutions.
        </motion.p>
        <motion.button
          whileHover={{
            scale: 1.05,
            background: "#fff",
            color: "#003366",
            borderColor: "#fff",
          }}
          whileTap={{ scale: 0.98 }}
          style={professionalStyles.ctaButton}
          onClick={() => navigate("/contact")}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.6, duration: 0.8 }}
        >
          Get In Touch
        </motion.button>
      </motion.div>

      {/* Why Choose Us Section */}
      <motion.section
        className="text-center"
        style={professionalStyles.whyUsSection}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.2 }}
        variants={fadeIn}
      >
        <motion.h2
          style={professionalStyles.sectionTitle}
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          WHY CHOOSE US
        </motion.h2>
        <Container>
          <Row className="g-4 mt-3">
            {[
              {
                title: "Proven Track Record",
                text: "Our investment fund trackings are the best performing in Australia.",
                image: WhyUs1,
              },
              {
                title: "Premium Service",
                text: "We offer exceptional investment and asset managers with a complete outsourcing solution.",
                image: WhyUs2,
              },
              {
                title: "Specialist Team",
                text: "Our internal investment management team provides expert care and oversight, ensuring optimal returns.",
                image: WhyUs3,
              },
              {
                title: "Responsible Investment",
                text: "We invest with a long-term perspective, considering the impact on communities and ecosystems.",
                image: WhyUs4,
              },
            ].map((item, index) => (
              <Col md={6} lg={3} key={index} className="d-flex">
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.15, duration: 0.6 }}
                  whileHover={{
                    y: -8,
                    boxShadow: "0 8px 25px rgba(0,0,0,0.15)",
                  }}
                  style={{ width: "100%" }}
                >
                  <Card style={professionalStyles.card}>
                    <motion.img
                      src={item.image}
                      alt={item.title}
                      style={professionalStyles.cardImage}
                    />
                    <Card.Body style={professionalStyles.cardBody}>
                      <Card.Title style={professionalStyles.cardTitle}>
                        {item.title}
                      </Card.Title>
                      <Card.Text style={professionalStyles.cardText}>
                        {item.text}
                      </Card.Text>
                    </Card.Body>
                  </Card>
                </motion.div>
              </Col>
            ))}
          </Row>
        </Container>
      </motion.section>

      {/* Investment & Asset Management Section */}
      <Container style={{ paddingTop: "80px", paddingBottom: "80px" }}>
        <Row className="align-items-center g-5">
          <Col md={6}>
            <motion.img
              src={InvestmentImage}
              alt="Investment & Asset Management"
              className="img-fluid"
              style={professionalStyles.serviceImage}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={slideInLeft}
              whileHover={{
                scale: 1.02,
                boxShadow: "0 8px 30px rgba(0,0,0,0.2)",
              }}
            />
          </Col>
          <Col md={6}>
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={slideInRight}
            >
              <h1 style={professionalStyles.serviceTitle}>
                Investment & Asset Management
              </h1>
              <h5 style={professionalStyles.serviceText}>
                We offer comprehensive investment management services, including
                asset allocation, financial statement analysis, stock selection,
                and portfolio strategy implementation. Our data-driven approach
                ensures optimal performance and risk management.
              </h5>
            </motion.div>
          </Col>
        </Row>
      </Container>

      {/* Responsible Entity Service Section */}
      <div style={{ background: "#f8f9fa" }}>
        <Container style={{ paddingTop: "80px", paddingBottom: "80px" }}>
          <Row className="align-items-center g-5">
            <Col md={6} className="order-md-1 order-2">
              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={slideInLeft}
              >
                <h1 style={professionalStyles.serviceTitle}>
                  Responsible Entity Service
                </h1>
                <h5 style={professionalStyles.serviceText}>
                  Optwise Group acts as Responsible Entity, assisting investment
                  managers with listed and unlisted funds for wholesale and
                  retail investors. We provide comprehensive compliance,
                  governance, and administrative support.
                </h5>
              </motion.div>
            </Col>
            <Col md={6} className="order-md-2 order-1">
              <motion.img
                src={CommercialImage}
                alt="Responsible Entity Service"
                className="img-fluid"
                style={professionalStyles.serviceImage}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={slideInRight}
                whileHover={{
                  scale: 1.02,
                  boxShadow: "0 8px 30px rgba(0,0,0,0.2)",
                }}
              />
            </Col>
          </Row>
        </Container>
      </div>
    </Container>
  );
};

export default HomePage;
